import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { GroupsAndReservationsDialogFormKeys, GroupsAndReservationsDialogFormValues } from './schema';
import { GROUP_PREFIX } from './constants';

/**
 * Synchronizes the selection of groups and their associated reservations.
 * When a group is selected, all its reservations are automatically selected.
 * When a group is deselected, all its reservations are automatically deselected.
 *
 * Also keeps track of reservations that were selected via a group
 * in the `reservationsWithGroupChecked` field, to allow excluding them on submit.
 *
 * @param groupToReservationMap - A mapping of group IDs to their associated reservation IDs.
 * @returns An array of reservation IDs that are disabled due to being associated with selected groups.
 */
export const useGroupReservationsSelectionSync = (groupToReservationMap: Record<string, string[]>) => {
    const { watch, setValue } = useFormContext<GroupsAndReservationsDialogFormValues>();
    const selectedGroupsAndReservationsIds = watch(GroupsAndReservationsDialogFormKeys.groupsAndReservations);
    const selectedGroupIds = useMemo(
        () => selectedGroupsAndReservationsIds.filter((id) => id.startsWith(GROUP_PREFIX)),
        [selectedGroupsAndReservationsIds]
    );
    const previousSelectedGroupsRef = useRef<string[]>([]);
    const [disabledReservationIds, setDisabledReservationIds] = useState<string[]>([]);

    useEffect(() => {
        const previousGroupIds = previousSelectedGroupsRef.current;
        const selectedIdsSet = new Set(selectedGroupsAndReservationsIds);

        selectedGroupIds.forEach((groupId) => {
            const reservationIds = groupToReservationMap[groupId] ?? [];
            reservationIds.forEach((rId) => selectedIdsSet.add(rId));
        });

        const deselectedGroups = previousGroupIds.filter((id) => !selectedGroupIds.includes(id));

        deselectedGroups.forEach((groupId) => {
            const reservationIds = groupToReservationMap[groupId] ?? [];
            reservationIds.forEach((rId) => {
                selectedIdsSet.delete(rId);
            });
        });

        const newSelectedIds = Array.from(selectedIdsSet);

        if (newSelectedIds.length !== selectedGroupsAndReservationsIds.length) {
            setValue(GroupsAndReservationsDialogFormKeys.groupsAndReservations, newSelectedIds);
        }

        const reservationsFromSelectedGroups = selectedGroupIds.flatMap(
            (groupId) => groupToReservationMap[groupId] ?? []
        );

        setValue(GroupsAndReservationsDialogFormKeys.reservationsWithGroupChecked, reservationsFromSelectedGroups);
        setDisabledReservationIds(reservationsFromSelectedGroups);
        previousSelectedGroupsRef.current = selectedGroupIds;
    }, [selectedGroupsAndReservationsIds, setValue, groupToReservationMap, selectedGroupIds]);

    return disabledReservationIds;
};
